/**
 * Player bar — shown above (P1) or below (P2) that player's equipment row.
 *
 * Left side: name + active marker (X) + HP heart + value + +/- + stamina ✦ +
 *   value + +/- + (optional) stance pill.
 * Right side: Discard count, Deck count, refresh icon (ready all equipment),
 *   deny/skull icon (destroy armor — placeholder for now).
 */
import { Heart, Sparkles, RotateCcw, Ban, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PlayerState } from "@/types/game";
import type { Card } from "@/types/card";

interface PlayerBarProps {
  player: PlayerState;
  cardMap: Map<string, Card>;
  isActive: boolean;
  onAdjustHealth: (delta: number) => void;
  onAdjustStamina: (delta: number) => void;
  onReadyAllEquip?: () => void;
  onDestroyArmor?: () => void;
  /** Click on the Deck pill — draws a card. */
  onDrawCard?: () => void;
  /** Click on the Discard pill — opens viewer modal. */
  onOpenDiscardPile?: () => void;
}

function StepBtn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-5 w-5 items-center justify-center rounded bg-[#22262f] text-[10px] font-bold text-[#94a3b8] hover:bg-[#2a2f3a] hover:text-[#e2e8f0]"
    >
      {children}
    </button>
  );
}

export function PlayerBar({
  player,
  cardMap,
  isActive,
  onAdjustHealth,
  onAdjustStamina,
  onReadyAllEquip,
  onDestroyArmor,
  onDrawCard,
  onOpenDiscardPile,
}: PlayerBarProps) {
  const stanceCard = player.stanceCardId ? cardMap.get(player.stanceCardId) : null;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 text-xs",
        isActive ? "text-[#e2e8f0]" : "text-[#94a3b8]",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn("font-semibold", isActive && "text-amber-soft")}>
          {player.name}
        </span>
        {isActive && <X size={11} className="text-amber-soft" aria-hidden />}

        {/* HP */}
        <span className="flex items-center gap-1">
          <Heart size={12} className="text-red-400" aria-hidden />
          <span className="tabular-nums font-semibold">{player.health}</span>
          <StepBtn onClick={() => onAdjustHealth(1)}>+</StepBtn>
          <StepBtn onClick={() => onAdjustHealth(-1)}>−</StepBtn>
        </span>

        {/* Stamina */}
        <span className="flex items-center gap-1">
          <Sparkles size={12} className="text-amber-soft" aria-hidden />
          <span className="tabular-nums font-semibold">
            {player.stamina}
            <span className="text-[#64748b]">/{Math.max(7, player.stamina)}</span>
          </span>
          <StepBtn onClick={() => onAdjustStamina(1)}>+</StepBtn>
          <StepBtn onClick={() => onAdjustStamina(-1)}>−</StepBtn>
        </span>

        {/* Stance pill */}
        {stanceCard && (
          <span className="rounded bg-teal-500/20 px-2 py-0.5 text-[10px] font-semibold text-teal-300">
            {stanceCard.name.length > 12
              ? stanceCard.name.slice(0, 10) + "…"
              : stanceCard.name}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 text-[11px] text-[#94a3b8]">
        {onOpenDiscardPile ? (
          <button
            type="button"
            onClick={onOpenDiscardPile}
            className="cursor-pointer rounded px-1 py-0.5 font-semibold hover:bg-[#22262f] hover:text-amber-soft"
            title="View discard pile"
          >
            Disc {player.discard.length}
          </button>
        ) : (
          <span>Disc {player.discard.length}</span>
        )}
        {onDrawCard ? (
          <button
            type="button"
            onClick={onDrawCard}
            className="cursor-pointer rounded px-1 py-0.5 font-semibold hover:bg-[#22262f] hover:text-amber-soft"
            title="Draw a card"
          >
            Deck {player.deckCards.length}
          </button>
        ) : (
          <span>Deck {player.deckCards.length}</span>
        )}
        {onReadyAllEquip && (
          <button
            type="button"
            onClick={onReadyAllEquip}
            className="rounded p-1 hover:bg-[#22262f] hover:text-[#e2e8f0]"
            title="Ready all equipment"
          >
            <RotateCcw size={13} />
          </button>
        )}
        {onDestroyArmor && (
          <button
            type="button"
            onClick={onDestroyArmor}
            className="rounded p-1 hover:bg-[#22262f] hover:text-[#e2e8f0]"
            title="Destroy armor"
          >
            <Ban size={13} />
          </button>
        )}
      </div>
    </div>
  );
}
